import { z } from "zod";
import { ENTITLEMENT_SOURCES, IEC_PLUS_TIERS } from "../../constants/subscription.js";
import {
	IsoDateTimeSchema,
	ServiceIdSchema,
	SubjectSchema,
	UuidV7Schema,
} from "../_shared/primitives.js";

/**
 * Контракты модуля `entitlements` — реестр прав уровня IEC+ (план §4.8).
 *
 * Инвариант границы модуля: `entitlements` хранит запись «`sub` имеет уровень
 * `plus|pro` в сервисе X до даты D, источник — такой-то» и НЕ решает, что этот
 * уровень означает внутри сервиса. Сервис берёт **максимум** из своей локальной
 * подписки и уровня отсюда.
 */

/** Уровень права. `free` — отсутствие права, отдельной записи в реестре не имеет. */
export const IecPlusTierSchema = z.enum(IEC_PLUS_TIERS);

/** Уровень, который реально лежит в реестре (записи `free` не существует). */
export const GrantedTierSchema = z.enum(["plus", "pro"]);

/**
 * Откуда взялось право.
 *
 * ПОЧЕМУ СПИСОК СОБИРАЕТСЯ ЗДЕСЬ, А НЕ В `constants/subscription.ts`.
 * Декрет Д7 добавил два источника — `programme_gift` (подарок IEC+ Pro на 12
 * месяцев, ОТДЕЛЬНЫЙ В КАЖДОМ СЕРВИСЕ) и `admin_grant` (ручная выдача из
 * админки). Прежние `programme` и `grant` остаются ради уже выданных прав:
 * `ALTER TYPE ... DROP VALUE` в Postgres не существует, а перекрашивать
 * историю нельзя. `ENTITLEMENT_SOURCES` — база подписочной части контракта и
 * живёт в зоне модуля `billing`, поэтому расширение лежит здесь.
 */
export const ENTITLEMENT_SOURCE_VALUES = [
	...ENTITLEMENT_SOURCES,
	"programme_gift",
	"admin_grant",
] as const;

export const EntitlementSourceSchema = z.enum(ENTITLEMENT_SOURCE_VALUES);

/**
 * Источники, которые человек вправе отменить сам из раздела «Подписки».
 * Подписочное право сюда не входит намеренно: его снимает `billing` по
 * окончании оплаченного периода, иначе кнопка в витрине отбирала бы уровень,
 * за который уже заплачено.
 */
export const USER_CANCELLABLE_SOURCES = ["programme_gift", "admin_grant"] as const;

/**
 * Пояснение источника для витрины: «Gift for Work and Travel USA · until 12.09.2027».
 * Текста здесь нет — только ключ i18n и параметры (rules/i18n.md).
 */
export const EntitlementSourceLabelSchema = z.object({
	/** Ключ вида `entitlements.source.programme`. */
	key: z.string().min(1).max(200),
	/** Параметры ICU: имя программы, дата, сервис. */
	params: z.record(z.string(), z.union([z.string(), z.number()])).default({}),
});

export const EntitlementSchema = z.object({
	id: UuidV7Schema,
	/** `null` = право действует во всех сервисах экосистемы. */
	serviceId: ServiceIdSchema.nullable(),
	tier: GrantedTierSchema,
	source: EntitlementSourceSchema,
	startsAt: IsoDateTimeSchema,
	/** `null` = бессрочно (админский грант без срока). */
	expiresAt: IsoDateTimeSchema.nullable(),
	/** Человекочитаемое пояснение источника — ключ i18n + параметры. */
	sourceLabel: EntitlementSourceLabelSchema,
	/** Ссылка на источник: подписка, программа Портала, грант админа. Не платёжные данные. */
	externalRef: z.string().max(200).nullable(),
});

export const EntitlementsMineOutputSchema = z.object({
	items: z.array(EntitlementSchema),
	/** Максимум по всем сервисам — то, что показывает шапка раздела «IEC+». */
	effectiveTier: IecPlusTierSchema,
	/** Ближайшая дата окончания среди активных прав; `null` — нечему истекать. */
	nearestExpiresAt: IsoDateTimeSchema.nullable(),
});

export const EntitlementsEffectiveTierInputSchema = z.object({
	serviceId: ServiceIdSchema,
});

export const EntitlementsEffectiveTierOutputSchema = z.object({
	/**
	 * За что посчитан уровень. `ecosystem` — максимум по ВСЕЙ экосистеме
	 * (`serviceId` не задан); `service` — максимум, применимый к одному сервису.
	 *
	 * Раньше в ответе без `serviceId` стояло `serviceId: "iec-id"`, и это
	 * читалось как «уровень в сервисе IEC ID» — то есть отвечало не на тот
	 * вопрос, который задали.
	 */
	scope: z.enum(["ecosystem", "service"]),
	/** `null` при `scope='ecosystem'`. */
	serviceId: ServiceIdSchema.nullable(),
	/** Максимум прав IEC ID по этому сервису. Локальную подписку сервис домешивает сам. */
	tier: IecPlusTierSchema,
	/** Какая запись реестра дала уровень; `null` при `tier='free'`. */
	entitlementId: UuidV7Schema.nullable(),
	source: EntitlementSourceSchema.nullable(),
	expiresAt: IsoDateTimeSchema.nullable(),
	sourceLabel: EntitlementSourceLabelSchema.nullable(),
});

/** Снимок прав для SET-события и для кэша профиля на стороне сервиса. */
export const EntitlementSnapshotSchema = z.object({
	subject: SubjectSchema,
	serviceId: ServiceIdSchema.nullable(),
	tier: IecPlusTierSchema,
	source: EntitlementSourceSchema.nullable(),
	expiresAt: IsoDateTimeSchema.nullable(),
	/**
	 * Продлится ли право САМО по окончании срока.
	 *
	 * Отвечает на единственный вопрос, который сервису нужен помимо уровня:
	 * человек остаётся или уходит. `false` + известный `expiresAt` — основание
	 * показать «продлите, иначе доступ закончится 5 октября»; `true` — молчать.
	 *
	 * Не платёжные данные и не состояние биллинга наружу: ни провайдера, ни
	 * суммы, ни способа оплаты здесь нет и не будет.
	 */
	renews: z.boolean(),
});

/**
 * Почему право изменилось.
 *
 * `trial_expired` заведён отдельно от `expired`, потому что это разные письма и
 * разные экраны: «пробный кончился, оформите подписку» и «подписка кончилась,
 * продлите» — не одно и то же, а различить их по снимку сервис уже не может
 * (истёкшего права в снимке нет).
 *
 * Набор ОТКРЫТ для читателя: гайд интеграции велит сервисам не ветвиться по
 * неизвестному значению, поэтому новое значение не ломает написанный приёмник.
 */
export const EntitlementChangeReasonSchema = z.enum([
	"granted",
	"revoked",
	"expired",
	"trial_expired",
	/**
	 * Человек отменил автопродление. Доступ при этом НЕ снимается — оплаченные
	 * (или пробные) дни он дожить обязан, — но `renews` у права стал `false`,
	 * и сервис может перестать предлагать апгрейд тому, кто уже ушёл.
	 */
	"renewal_cancelled",
	"renewed",
	"downgraded",
	"upgraded",
]);

// ─────────────────────── entitlements.cancel (витрина) ───────────────────────

/**
 * Отмена подарка или админского гранта самим человеком (декрет Д7).
 *
 * Отдельной причины на входе нет намеренно: в аудит уходит машинный код
 * `user_cancelled`, а свободный текст из браузера в журнале решений о правах
 * — это поле для чужого ввода в отчётах поддержки.
 */
export const EntitlementsCancelInputSchema = z.object({
	entitlementId: UuidV7Schema,
});

export const EntitlementsCancelOutputSchema = z.object({
	/** `false` — право уже было снято; повтор безопасен и не считается ошибкой. */
	cancelled: z.boolean(),
	/** Уровень ПОСЛЕ отмены: витрина показывает его без второго запроса. */
	effectiveTier: IecPlusTierSchema,
});
